import { useState } from "react";
import { type MessageProps } from "../common/components/Messages/Message";
import { registerUser } from "./api/registerUserForFree";
import { redirectToUrl } from "../utils/url/urlUtils";
import { useBooleanState } from "../common/hooks/useBooleanState";
import { CAPTCHA_SERVER_ERROR } from "../utils/constants";
import { trackRegisterForFree } from "../feature/Analytics/ga/ga";
import { type FieldError, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";

interface FormFields {
  email: string;
  captcha: string;
  generalTerms: boolean;
  privacyPolicy: boolean;
  marketingConsent: boolean;
  isRestrictedDomainLimitExceeded?: boolean;
}

export const useRegisterForFree = () => {
  const {
    register,
    setError,
    formState: { errors },
    watch,
    setValue,
    control,
    handleSubmit,
  } = useForm<FormFields>({
    defaultValues: {
      email: "",
      captcha: "",
      generalTerms: false,
      privacyPolicy: false,
      marketingConsent: false,
    },
  });

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);
  const [restrictedDomainAttemptsCount, setRestrictedDomainAttemptsCount] = useState(1);

  const setSubmissionError = (errorString: string) => {
    if (errorString == CAPTCHA_SERVER_ERROR) {
      setError("captcha", { message: errorString });
    } else {
      setError("email", { message: errorString });
      if (errorString === "error.restrictedDomain") {
        setRestrictedDomainAttemptsCount((count: number) => count + 1);
      }
    }
  };

  const onSubmitHandler = handleSubmit(async (values) => {
    trackRegisterForFree("Register for Free");

    if (restrictedDomainAttemptsCount >= 3) {
      values.isRestrictedDomainLimitExceeded = true;
    }
    const response = await registerUser(values);
    if (response.error) {
      setSubmissionError(response.error);
    } else {
      redirectToUrl("/registered/user/confirmation");
    }
  });

  const onResetClick = (email: string) => {
    setValue("email", email);
    closeLogin();
    openReset();
  };

  return {
    errors,
    watch,
    control,
    resetPopupOpen,
    loginPopupOpen,
    openLogin,
    closeLogin,
    closeReset,
    onResetClick,
    onSubmitHandler,
  };
};

type SpanProps = JSX.IntrinsicElements["span"];

interface HelperLabelErrorMessageProps extends SpanProps {
  error?: FieldError;
}

export const HelperLabelErrorMessage = ({ error, ...rest }: HelperLabelErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return null;
  }

  return <span {...rest}>{t(error.message)}</span>;
};

interface ErrorMessageProps extends Omit<MessageProps, "type" | "children" | "message"> {
  error?: FieldError;
}

export const ErrorMessage = ({ error, ...rest }: ErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return null;
  }

  return (
    <Alert severity={"error"} {...rest}>
      {t(error.message)}
    </Alert>
  );
};

export const RegisterContactSalesSection = () => (
  <p className={"scha__register-free__contact-sales"}>
    <Trans i18nKey={"page.registerFree.contactSales"}>
      Are you already a Schaeffler customer or sales partner? Please contact your Schaeffler sales representative for
      free access to <span className={"scha__register-free__contact-sales__logo"}>medias</span>{" "}
      <span className={"scha__register-free__contact-sales__logo2"}>Business</span>.
    </Trans>
  </p>
);
